/**
 * SSE frame parsing (D4).
 *
 * **Pure: no fetch, no React, no knowledge of turns.** Bytes in, frames out. That is what
 * makes the one bug this design exists to prevent — a frame split across two network
 * chunks — testable against string fixtures instead of against a live turn.
 *
 * `EventSource` would give this away for free and cannot be used at all (D4): the turn is a
 * `POST` and it needs an `Authorization` header. So the ~80 lines are written here, to the
 * WHATWG spec's own rules:
 *
 * - lines end with `\r\n`, `\n` or a lone `\r`, and the pair may straddle a chunk boundary;
 * - a line starting with `:` is a comment — which for this API is the 15s `: ping`
 *   heartbeat, and it is **surfaced rather than dropped**, because time-since-last-byte is
 *   how the client tells "the DM is thinking" from "the connection died" (D4);
 * - `field: value`, with exactly one leading space stripped from the value;
 * - `data` accumulates across lines and joins with `\n`;
 * - a blank line dispatches, and dispatches nothing if no `data` line was seen.
 *
 * What it deliberately does *not* do: reconnect, honour `retry:` (a retried POST is a second
 * turn — P2), or validate payloads. `retry` is parsed and carried only so that a server that
 * starts sending it does not read as an unknown field.
 */

/** One thing the decoder is willing to hand out. */
export type SseFrame =
  | {
      kind: 'event';
      /** `null` when the frame carried no `event:` line — not something this API sends. */
      event: string | null;
      /** The `data:` lines, joined with `\n`. Never `undefined`; may be `''`. */
      data: string;
      /** The stream's last seen `id:`, per spec — it persists across frames. */
      id: string | null;
      retry: number | null;
    }
  | { kind: 'comment'; text: string };

/**
 * The incremental half, exposed on its own because every interesting test is synchronous:
 * feed it deliberately awful chunk boundaries and assert on what comes out.
 */
export class SseDecoder {
  #buffer = '';
  #data: string[] = [];
  #event: string | null = null;
  #id: string | null = null;
  #retry: number | null = null;

  /** Feed one decoded text chunk. Returns whatever became complete because of it. */
  push(chunk: string): SseFrame[] {
    this.#buffer += chunk;
    return this.#drain(false);
  }

  /**
   * End of stream. Any complete lines still buffered are processed; a final *incomplete*
   * frame is discarded, exactly as the spec says — a truncated terminal event is a dropped
   * connection, and `runTurn` reports it as one rather than acting on half a chapter.
   */
  flush(): SseFrame[] {
    return this.#drain(true);
  }

  #drain(final: boolean): SseFrame[] {
    const frames: SseFrame[] = [];
    for (const line of this.#takeLines(final)) {
      const frame = this.#line(line);
      if (frame) frames.push(frame);
    }
    if (final) this.#buffer = '';
    return frames;
  }

  #takeLines(final: boolean): string[] {
    const lines: string[] = [];
    let start = 0;
    let index = 0;

    while (index < this.#buffer.length) {
      const char = this.#buffer[index];
      if (char === '\n') {
        lines.push(this.#buffer.slice(start, index));
        index += 1;
        start = index;
      } else if (char === '\r') {
        // A trailing lone `\r` may be the first half of a `\r\n` that lands in the next
        // chunk. Holding it is the entire chunk-boundary bug, in one branch.
        if (index === this.#buffer.length - 1 && !final) break;
        lines.push(this.#buffer.slice(start, index));
        index += this.#buffer[index + 1] === '\n' ? 2 : 1;
        start = index;
      } else {
        index += 1;
      }
    }

    this.#buffer = this.#buffer.slice(start);
    return lines;
  }

  #line(line: string): SseFrame | null {
    if (line === '') return this.#dispatch();
    if (line.startsWith(':')) return { kind: 'comment', text: strip(line.slice(1)) };

    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    const value = colon === -1 ? '' : strip(line.slice(colon + 1));

    switch (field) {
      case 'data':
        this.#data.push(value);
        break;
      case 'event':
        this.#event = value;
        break;
      case 'id':
        // Spec: an id containing a NUL is ignored outright.
        if (!value.includes('\0')) this.#id = value;
        break;
      case 'retry':
        if (/^\d+$/.test(value)) this.#retry = Number(value);
        break;
      default:
        break; // Unknown fields are ignored, not fatal.
    }
    return null;
  }

  #dispatch(): SseFrame | null {
    // No `data:` line means there is nothing to dispatch — but the event name still resets,
    // which is why this cannot just be an early return.
    if (this.#data.length === 0) {
      this.#event = null;
      return null;
    }
    const frame: SseFrame = {
      kind: 'event',
      event: this.#event,
      data: this.#data.join('\n'),
      id: this.#id,
      retry: this.#retry,
    };
    this.#data = [];
    this.#event = null;
    return frame;
  }
}

/** Exactly one leading space is part of the framing, not of the value. */
function strip(value: string): string {
  return value.startsWith(' ') ? value.slice(1) : value;
}

/**
 * Bytes → frames. Takes an `AsyncIterable` rather than a `ReadableStream` so tests can feed
 * it hand-cut `Uint8Array`s, including ones that split a multi-byte character in half.
 */
export async function* decodeSseFrames(
  source: AsyncIterable<Uint8Array>,
): AsyncGenerator<SseFrame> {
  const frames = new SseDecoder();
  // One `TextDecoder` for the whole stream, in streaming mode: a multi-byte character split
  // across two chunks is otherwise two replacement characters in the middle of narration.
  const text = new TextDecoder();

  for await (const chunk of source) {
    yield* frames.push(text.decode(chunk, { stream: true }));
  }

  yield* frames.push(text.decode());
  yield* frames.flush();
}

/**
 * A `ReadableStream` as an async iterable.
 *
 * Node iterates one natively; browsers still do not, and the turn stream is the one place
 * where "works in the test runner, throws in Safari" would cost a real turn.
 */
export async function* streamChunks(
  stream: ReadableStream<Uint8Array>,
): AsyncGenerator<Uint8Array> {
  const reader = stream.getReader();
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) return;
      if (value) yield value;
    }
  } finally {
    // Releasing before cancelling would throw; cancelling a finished stream is a no-op.
    // This is what closes the connection when the caller breaks out on the terminal event.
    await reader.cancel().catch(() => undefined);
  }
}
